import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    // Toggle loader on submit button (rendered in Liquid)
    const submitButton = form.querySelector('.cart-discount__button');
    /** @type {HTMLElement | null} */
    const spinner = submitButton instanceof HTMLElement ? submitButton.querySelector('.cart-discount__spinner') : null;
    /** @type {HTMLElement | null} */
    const label = submitButton instanceof HTMLElement ? submitButton.querySelector('.cart-discount__button-label') : null;
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.disabled = true;
      if (label instanceof HTMLElement) label.classList.add('hidden');
      if (spinner instanceof HTMLElement) spinner.classList.remove('hidden');
    }

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.removeAttribute('aria-busy');
        if (spinner instanceof HTMLElement) spinner.classList.add('hidden');
        if (label instanceof HTMLElement) label.classList.remove('hidden');
        submitButton.disabled = false;
      }
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

(function() {
  /** @type {any} */
  const w = window;
  if (w.__offersModalBound) return;
  w.__offersModalBound = true;

  function getModal() {
    return /** @type {HTMLDialogElement | null} */ (document.getElementById('custom-modal'));
  }

  /**
   * @param {HTMLDialogElement | null} modal
   */
  async function closeWithAnimation(modal) {
    if (!modal || !modal.open) return;
    modal.classList.add('dialog-closing');
    await new Promise((/** @type {(value: void) => void} */ resolve) => {
      /** @param {AnimationEvent} e */
      const onEnd = (e) => {
        if (e.target === modal) resolve();
      };
      modal.addEventListener('animationend', onEnd, { once: true });
      // Fallback in case animationend doesn't fire
      setTimeout(() => resolve(), 600);
    });
    modal.close();
    modal.classList.remove('dialog-closing');
  }
  /**
   * @param {string} discountCodeValue
   * @param {string | undefined} sectionId
   * @param {string[]} [existingDiscounts]
   */
  async function applyDiscountAndRender(discountCodeValue, sectionId, existingDiscounts = []) {
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        discount: [...existingDiscounts, discountCodeValue].join(','),
        sections: sectionId ? [sectionId] : [],
      }),
    });
  
    const data = await response.json();
  
    try {
      document.dispatchEvent(new DiscountUpdateEvent(data, sectionId || '')); // event expects string
    } catch (e) {}
  }

  /**
   * @param {string} code
   * @param {HTMLElement | null} button
   */
  async function applyNative(code, button) {
    if (button instanceof HTMLButtonElement) {
      button.classList.add('is-loading');
      const spinner = /** @type {HTMLElement | null} */ (button.querySelector('.offers-apply__spinner'));
      const label = /** @type {HTMLElement | null} */ (button.querySelector('.offers-apply__label'));
      if (spinner) {
        spinner.classList.remove('hidden');
        spinner.style.marginInlineStart = '8px';
      }
      if (label) {
        label.classList.add('not-active');
      }
    }
  
    try {
      const modal = getModal();
      const discountComponent = /** @type {HTMLElement | null} */ (
        (modal && modal.querySelector('cart-discount-component')) ||
        document.querySelector('cart-discount-component')
      );
  
      const sectionId = discountComponent?.dataset?.sectionId;
  
      const existingDiscounts = [];
      if (discountComponent) {
        for (const pill of Array.from(discountComponent.querySelectorAll('.cart-discount__pill'))) {
          if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
            existingDiscounts.push(pill.dataset.discountCode);
          }
        }
      }
  
      await applyDiscountAndRender((code || '').trim(), /** @type {string} */ (sectionId || ''), existingDiscounts);
    } catch (e) {
      // no-op; UI reset below
    } finally {
      if (button instanceof HTMLButtonElement) {
        const spinner = /** @type {HTMLElement | null} */ (button.querySelector('.offers-apply__spinner'));
        const label = /** @type {HTMLElement | null} */ (button.querySelector('.offers-apply__label'));
        if (spinner) spinner.classList.add('hidden');
        if (label) label.classList.remove('hidden');
        button.classList.remove('is-loading');
      }
    }
  }

  document.addEventListener('click', function(/** @type {MouseEvent} */ e) {
    const target = /** @type {HTMLElement | null} */ (e.target instanceof HTMLElement ? e.target : null);
    const closeBtn = target?.closest('.cart-drawer__close-modal-button');
    if (closeBtn) {
      e.preventDefault();
      const modal = getModal();
      if (modal) closeWithAnimation(modal);
      return;
    }

    const applyBtn = target?.closest('.offers-list__apply-button');
    if (applyBtn) {
      e.preventDefault();
      applyNative(/** @type {HTMLElement} */ (applyBtn).dataset.code || '', /** @type {HTMLElement} */ (applyBtn));
      return;
    }

    const continueBtn = target?.closest('#offers-continue-button');
    if (continueBtn) {
      e.preventDefault();
      const modal = getModal();
      if (modal && modal.open) closeWithAnimation(modal);
    }
  }, true);

  document.addEventListener('cancel', function(/** @type {Event} */ e) {
    const modal = /** @type {EventTarget | null} */ (e.target);
    if (modal instanceof HTMLDialogElement && modal.id === 'custom-modal') {
      e.preventDefault();
      closeWithAnimation(modal);
    }
  });
// document.querySelector('.cart-drawer__open-modal-button')?.addEventListener('click',(e)=>{

//   const modal = getModal();
//   const button = document.querySelector('.offers-list__apply-button[data-code="NEW10"]');
//   if(!button || !modal)return;
//   if (!button.disabled){
//     applyNative('NEW10',button);
//     modal.close()
//   }else{
//     modal.showModal()
//   }
// })

})();